import { getConfig } from '../config';

/**
 * API Service for making HTTP requests to the backend
 */
class ApiService {
    static config = null;

    /**
     * Initialize the API service with the configuration
     */
    static async init() {
        if (!this.config) {
            this.config = await getConfig();
            console.log('API Service initialized with endpoint:', this.config.apiBaseUrl);
        }
        return this.config;
    }

    /**
     * Make an HTTP request to the API
     *
     * @param {string} endpoint - API endpoint (without base URL)
     * @param {Object} options - Fetch options
     * @returns {Promise} Promise with the response data
     */
    static async request(endpoint, options = {}) {
        await this.init();

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        const token = localStorage.getItem('token');
        console.log('Token from localStorage:', token ? 'Present (length: ' + token.length + ')' : 'Not found');

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        console.log('Request headers:', JSON.stringify(headers));

        const url = `${this.config.apiBaseUrl}${endpoint}`;
        console.log('Request URL:', url);

        try {
            console.log('Making fetch request with method:', options.method || 'GET');
            const response = await fetch(url, {
                ...options,
                headers,
            });

            console.log('Response status:', response.status);
            console.log('Response headers:', JSON.stringify(Array.from(response.headers.entries())));

            if (!response.ok) {
                console.error('Response error:', response.status, response.statusText);
                const errorData = await response.json().catch(e => {
                    console.error('Error parsing error response:', e);
                    return {};
                });
                console.error('Error data:', JSON.stringify(errorData));
                throw new Error(
                    errorData.message || `HTTP error! Status: ${response.status}`
                );
            }

            return await response.json();
        } catch (error) {
            console.error(`API request failed for ${endpoint}:`, error.name, error.message);
            console.error('Full error:', error);
            throw error;
        }
    }

    static async listEnvironments() {
        return this.request('/environments');
    }

    static async getEnvironment(name) {
        return this.request(`/environments/${name}`);
    }

    static async getEnvironmentState(name) {
        return this.request(`/environments/${name}/state`);
    }

    static async startEnvironment(name) {
        return this.request(`/environments/${name}/start`, { method: 'POST' });
    }

    static async stopEnvironment(name) {
        return this.request(`/environments/${name}/stop`, { method: 'POST' });
    }

    static async createEnvironment(data) {
        return this.request('/environments', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    static async updateEnvironment(name, data) {
        return this.request(`/environments/${name}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    static async deleteEnvironment(name) {
        return this.request(`/environments/${name}`, { method: 'DELETE' });
    }

    static async getSchedule(environmentName) {
        return this.request(`/environments/${environmentName}/schedule`);
    }

    static async updateSchedule(environmentName, scheduleData) {
        return this.request(`/environments/${environmentName}/schedule`, {
            method: 'PUT',
            body: JSON.stringify(scheduleData),
        });
    }

    static async listClusters() {
        return this.request('/clusters');
    }

    static async getCluster(id) {
        return this.request(`/clusters/${id}`);
    }

    static async getClustersForEnvironment(environmentId) {
        return this.request(`/environments/${environmentId}/clusters`);
    }

    static async addCluster(clusterData) {
        return this.request(`/clusters`, {
            method: 'POST',
            body: JSON.stringify(clusterData),
        });
    }

    static async updateCluster(id, data) {
        return this.request(`/clusters/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    static async deleteCluster(clusterId) {
        return this.request(`/clusters/${clusterId}`, {
            method: 'DELETE',
        });
    }
}

export default ApiService;